/**
 * Building a document's session WITHOUT opening it (VE-31006).
 *
 * Redact All burns every file whose released output is behind what the
 * reviewer decided. `runDocumentBurnIn(tdoId, session)` can already do that
 * for any file — it takes the tdo and the session as arguments, both
 * `selectSession` reads inside it are defensive, and VE-30105/30104 moved
 * Bates and watermark governance to resolve for the tdo rather than for
 * whatever is on screen. What was missing is the session to hand it for a
 * document the editor has never loaded. This builds one.
 *
 * WHY NOT JUST OPEN EACH FILE. Opening is the obvious route and it is wrong
 * twice over. `OPEN_DOCUMENT_SUCCESS` fires the Artifex licence meter
 * unconditionally (see `licenseMeter.ts`), so a thirty-file run would bill
 * thirty licence events for one press of one button — a commercial
 * consequence, not a performance one. It also runs `watermarkAutoApply` per
 * open and drags the editor through thirty documents on screen.
 *
 * THE REDUCER IS THE SESSION FACTORY, so this runs it rather than
 * reimplementing it. The shape of a session is a hundred lines of literal in
 * `OPEN_DOCUMENT_SUCCESS`, and a second copy here would be a second answer to
 * "what is a session" that drifts the first time either changes — which is
 * the defect this codebase has spent three review rounds removing from
 * watermarks. `createReducer` is a pure function, so feeding it a fresh state
 * and the same ACTIONS the open path dispatches yields the session an open
 * would have produced, and keeps doing so for free.
 *
 * Note the plural. An open is `OPEN_DOCUMENT_SUCCESS` and THEN the PII
 * results as their own action; reproducing only the first is how this came to
 * publish documents with every approved PII redaction missing. "Same action,
 * same reducer" buys reducer-equivalence and nothing more — whatever the open
 * SAGA does afterwards has to be done here too, or named as deliberately
 * absent.
 *
 * NEVER-OPENED FILES COME BACK NULL, and that is a product rule rather than a
 * technical limit. Burning a file with no saved redaction data publishes a PDF
 * identical to the original and stamps it as a redacted output — the system
 * asserting "reviewed, nothing needed" about a file nobody has looked at. The
 * act being skipped is the looking, not the clicking. An asset the UPLOAD
 * pipeline stamped is not a save either (`isMachineWrittenRedactionAsset`),
 * which is the same signal `watermarkAutoApply` reads, so both paths agree on
 * what "this file has been worked on" means.
 *
 * UNLESS THE REVIEWER ASKS (`allowUnworked`). What the rule protects against
 * is the SYSTEM making that assertion by itself; a reviewer who has been
 * shown the count and ticked the box is making it themselves, which is the
 * one thing that was missing. Such a file builds a session with NOTHING in
 * it — no redactions, no page removals, no per-file marks — because there is
 * nothing to carry: the burn exists to put the SET's watermarks and
 * numbering on its pages, and those come from governance rather than from
 * the session. Only the caller that read the reviewer's answer may pass it;
 * see `includedUnworked` in the work list.
 */
import { call, select } from 'typed-redux-saga/macro';

import { selectWatermarkProjectFor } from '@redact-modules/projects/renderedWatermarks';
import type { TDOId } from '@common-modules/universal/models/Brands';
import { OPEN_DOCUMENT_SUCCESS, PII_RESULTS_LOADED } from '../actions';
import reducer from '../reducers';
import {
  isMachineWrittenRedactionAsset,
  serviceFetchTdoMediaInfo,
  serviceLoadDocumentRedactionData,
} from '../services';
import {
  appliedLexiconsFromAssetBody,
  batesStampFromAssetBody,
  batesStampOffFromAssetBody,
  excludedPagesFromAssetBody,
  lexiconDetectionsFromAssetBody,
  pageRedactsFromAssetBody,
  piiMatchesFromAssetBody,
  redactionsFromAssetBody,
  stripLegacyGlyphPad,
  watermarksFromAssetBody,
  watermarksOffFromAssetBody,
} from '../helpers';
import { isAssetFormatSupported } from '../store';
import type { SessionState } from '../models';
import { selectToken, selectGraphQLConfig } from './auth';
import { selectConfigEngines } from '@common-modules/engines/selectors';
import { fetchPiiDetections } from './runPii';
import { hydrateDocumentWatermarkImages } from './watermarkImageHydration';

/**
 * Why a file produced no session, for a caller that has to say so.
 *
 * `unworked` is the ordinary outcome and not an error: it is every file in the
 * request nobody has opened, and a Redact All over a folder will meet far more
 * of them than it acts on.
 */
export type HeadlessSessionSkip =
  | 'unworked'
  | 'unsupported-format'
  | 'unreadable';

export type HeadlessSessionResult =
  | {
      readonly ok: true;
      readonly session: SessionState;
      /**
       * Did this session come from NOTHING — no save a person made?
       *
       * THE LIVE ANSWER, and the only one in the system. The work list's
       * `includedUnworked` is a PERMISSION derived from rows fetched at the
       * press; this is read from the asset moments before the burn. They
       * disagree whenever anybody saved in between — another tab, a
       * colleague — and when they do, the caller must stamp what was
       * actually burned, not what it expected to burn. Handing the
       * permission to the burn instead meant a colleague's real redactions
       * were rendered, and then the output, the forced save and the
       * chain-of-custody CSV all recorded that nobody had reviewed the
       * file (review round 3, T2).
       */
      readonly unreviewed: boolean;
    }
  | { readonly ok: false; readonly reason: HeadlessSessionSkip };

export function* buildHeadlessSession(
  tdoId: TDOId,
  options?: {
    /**
     * Build a session for a file nobody has worked on, instead of refusing
     * it. See the header — this is the reviewer's own assertion, and only a
     * caller holding their answer may set it.
     */
    readonly allowUnworked?: boolean;
  }
) {
  const allowUnworked = !!options?.allowUnworked;
  const token = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);

  // THE AUTHORIZATION CHECK AS WELL AS THE FETCH. This throws on any GraphQL
  // error including a denial, so reaching past it proves the caller may read
  // this file. The open path makes the same point about the same call
  // (VE-26730 / SECURITY-11) — but where that one degrades to an editor with
  // no PDF, a burn with no PDF has nothing to render and must not proceed.
  let media;
  try {
    media = yield* call(
      serviceFetchTdoMediaInfo,
      tdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
  } catch (err) {
    console.warn(
      '[redact] headless session: media info unreadable',
      err instanceof Error ? err.name : 'unknown error'
    );
    return { ok: false as const, reason: 'unreadable' as const };
  }
  if (!media.mediaUri) {
    return { ok: false as const, reason: 'unreadable' as const };
  }

  let loaded;
  try {
    loaded = yield* call(
      serviceLoadDocumentRedactionData,
      tdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
  } catch (err) {
    console.warn(
      '[redact] headless session: redaction data unreadable',
      err instanceof Error ? err.name : 'unknown error'
    );
    return { ok: false as const, reason: 'unreadable' as const };
  }
  /**
   * Nothing saved, or nothing a PERSON saved — the two ways a file is
   * unworked, answered the same way. Refused by default; built empty when
   * the reviewer has asked for it.
   *
   * EMPTY OF DECISIONS, not of bookkeeping — the distinction is the whole
   * of VE-32440's finding B2, and the two halves pull opposite ways.
   *
   * NO GEOMETRY. A lexicon picked in the upload dialog is a suggestion
   * nobody has reviewed, and burning its matches in here would release
   * redactions on the reviewer's behalf under a dialog promising the
   * opposite. `lexiconDetections` stays empty and so does everything else
   * that puts a rectangle on a page: the file goes in as it is, carrying
   * only what the SET puts on it.
   *
   * BUT THE TO-DO NOTE TRAVELS. An upload stub is not only a suggestion —
   * `lexiconApply` writes its records with `evaluatedAt: null`, and that
   * null is how `watchFirstOpen` knows, on the first human open, that this
   * file still owes a lexicon run. The records are the note; the rects are
   * the work. Emptying the note here did not skip the work — the forced
   * pre-burn save (`saveBeforeExport` → `persistSessionSnapshot`) persisted
   * the empty list over the stub, so a reviewer opening the file a month
   * later found nothing pending and the org's configured auto-redaction
   * never ran. Including a file for the release quietly cancelled a
   * standing policy about it, with nothing on screen saying so.
   *
   * So they are carried through untouched, `evaluatedAt: null` included.
   * The burn is unaffected — it reads redactions and detections, not this —
   * and the next open does exactly what it would have done had the file
   * never been included.
   *
   * WHAT IS STILL NOT DONE, deliberately: the RELEASE does not carry those
   * auto-redactions. That one is inherent rather than an oversight —
   * evaluating a lexicon needs page text, and extracting page text per file
   * inside the pass is "open every document", which is the cost this whole
   * module exists to avoid (and would bill a licence event each; see the
   * header). A reviewer who needs them applied has to open the file, which
   * is the act they chose to skip.
   */
  const unworked = !loaded || isMachineWrittenRedactionAsset(loaded.details);
  if (unworked && !allowUnworked) {
    return { ok: false as const, reason: 'unworked' as const };
  }
  const loadedFormat = (loaded?.body as { assetFormatVersion?: string })
    ?.assetFormatVersion;
  if (!unworked && !isAssetFormatSupported(loadedFormat)) {
    // The open path warns and opens empty. Here an empty session would burn a
    // file as though the reviewer had decided nothing, so it is a skip.
    //
    // NOT ASKED OF AN UNWORKED FILE that is being included on purpose. The
    // gate is about trusting a body to describe REDACTIONS, and nothing in
    // that body reaches the burn on this path — only the lexicon records are
    // read from it, through `appliedLexiconsFromAssetBody`, which validates
    // every record it returns. A format too old to interpret as geometry is
    // still safe to carry a to-do note out of.
    return { ok: false as const, reason: 'unsupported-format' as const };
  }

  const savedState =
    unworked || !loaded
      ? // NO DECISIONS, deliberately — see `allowUnworked` in the header.
        {
          schemaVersion: 1 as const,
          redactions: [],
          detectionRectOverrides: {},
          pageRedacts: [],
          excludedPages: [],
          detectionState: [],
          piiMatches: [],
          lexiconDetections: [],
          /**
           * THE ONE THING CARRIED FORWARD: the note saying which lexicons
           * this file still owes a run, which the save below would
           * otherwise erase. Records only — the matches they will produce
           * are not in them, so nothing is redacted by carrying them, and
           * `lexiconDetections` above stays empty. See the header.
           */
          appliedLexicons: loaded
            ? appliedLexiconsFromAssetBody(loaded.body)
            : // No asset at all: no note to carry, and nothing picked any
              // lexicons for this file at upload.
              [],
          watermarks: [],
          watermarksOff: false,
          batesStamp: undefined,
          batesStampOff: undefined,
        }
      : {
          schemaVersion: 1 as const,
          redactions: stripLegacyGlyphPad(
            redactionsFromAssetBody(loaded.body),
            loadedFormat
          ),
          detectionRectOverrides: {},
          pageRedacts: pageRedactsFromAssetBody(loaded.body),
          excludedPages: excludedPagesFromAssetBody(loaded.body),
          detectionState: [],
          piiMatches: piiMatchesFromAssetBody(loaded.body),
          lexiconDetections: lexiconDetectionsFromAssetBody(loaded.body),
          appliedLexicons: appliedLexiconsFromAssetBody(loaded.body),
          watermarks: watermarksFromAssetBody(loaded.body),
          watermarksOff: watermarksOffFromAssetBody(loaded.body),
          batesStamp: batesStampFromAssetBody(loaded.body) ?? undefined,
          batesStampOff: batesStampOffFromAssetBody(loaded.body) ?? undefined,
        };

  // THE SAME ACTION THE OPEN PATH DISPATCHES, through the same reducer, over a
  // fresh state. `versionHistory` is deliberately empty: the burn computes the
  // version it is about to produce with its own round trip
  // (`computeNextVersion`), so nothing downstream of here reads it, and
  // fetching it would be a service call per file for a value nobody wants.
  const built = reducer(
    undefined,
    OPEN_DOCUMENT_SUCCESS({
      tdoId,
      documentName: media.name ?? `Document ${tdoId}`,
      pdfUrl: media.mediaUri,
      mediaAssetId: media.mediaAssetId,
      mediaAssetEngineId: media.mediaAssetEngineId,
      totalPages: 1,
      detections: [],
      savedState,
      versionHistory: [],
      // Null for an included-unworked file: there is no save to report,
      // which is the literal truth about it and what every reader of this
      // field (`watermarkAutoApply`, `lexiconAutoApply`) already expects of
      // a document nobody has opened.
      lastSavedAt: unworked ? null : (loaded?.createdDateTime ?? null),
      lastSavedBurnInVersion: undefined,
      piiMatches: savedState.piiMatches,
      vendorRequestIds: media.vendorRequestIds,
      folderId: media.folderId,
      folderName: media.folderName,
    })
  );
  /**
   * THE ENGINE'S FINDINGS, AND THE REVIEWER'S DECISIONS ABOUT THEM.
   *
   * An open is not one action. `OPEN_DOCUMENT_SUCCESS` deliberately carries
   * `detections: []` — the open path's own comment says the engine's results
   * are loaded separately — and they arrive a moment later as their own
   * action, which the reducer applies the SAVED selections to.
   *
   * Reproducing only the first action produced a session with no PII rows at
   * all, and `buildRedactionPlan` sources every `origin: 'auto'` rect from
   * exactly that array. The file burned, published, took the version and
   * reported itself CURRENT with every approved PII redaction missing from
   * it — and the forced pre-burn save then rewrote the asset from the same
   * empty array, so the reviewer's selections were gone from the editor too.
   *
   * So: the same SEQUENCE, through the same reducer.
   *
   * No results is not a failure. Plenty of documents have never been through
   * detection, and one whose asset carries no `piiMatches` has nothing to
   * re-apply.
   */
  // NOT FOR AN INCLUDED-UNWORKED FILE. Its saved selections are the empty
  // set by construction, so the reducer would apply nothing to the rows this
  // fetches — a request per file for a list nothing reads. Skipping it is
  // also what keeps the promise the dialog makes: no redactions.
  const { documentPIIDetectionEngineId } = yield* select(selectConfigEngines);
  const piiDetections =
    !unworked && documentPIIDetectionEngineId
      ? yield* call(
          fetchPiiDetections,
          tdoId,
          documentPIIDetectionEngineId,
          media.mediaUri
        )
      : null;
  const session = (
    piiDetections && piiDetections.length > 0
      ? reducer(built, PII_RESULTS_LOADED({ tdoId, detections: piiDetections }))
      : built
  ).session;
  if (!session) {
    // Unreachable: the reducer always installs a session for this action.
    // Asserted rather than assumed, because the alternative is burning from
    // whatever the editor happens to be showing.
    return { ok: false as const, reason: 'unreadable' as const };
  }

  // The drawn form of every image watermark, rebuilt the same way an open
  // rebuilds it — see `hydrateDocumentWatermarkImages`. Without this an image
  // mark reaches the burn with no `imageDataUrl`, `toBurnInWatermark` drops
  // it, and the file is released without marking it claims to carry.
  const watermarkProjectTdoId = yield* select(selectWatermarkProjectFor(tdoId));
  const { images, failedInstanceIds } = yield* call(
    hydrateDocumentWatermarkImages,
    {
      tdoId,
      marks: session.watermarks,
      watermarkProjectTdoId: watermarkProjectTdoId ?? null,
      token,
      cfg,
    }
  );
  const drawnById = new Map(images.map((i) => [i.instanceId, i]));
  return {
    ok: true as const,
    // What it was actually built from — see the type's own note.
    unreviewed: unworked,
    session: {
      ...session,
      watermarks: session.watermarks.map((wm) => {
        const drawn = drawnById.get(wm.instanceId);
        return drawn
          ? {
              ...wm,
              imageDataUrl: drawn.imageDataUrl,
              imageBaseDataUrl: drawn.imageBaseDataUrl ?? wm.imageBaseDataUrl,
            }
          : wm;
      }),
      // Carried so the caller can report artwork this pass could not rebuild,
      // exactly as the panel does for an open document.
      watermarkImageRestoreFailures: [...failedInstanceIds],
    },
  };
}
